import { CNPJValidation } from "helpers/validators";
import { cleanCpfCnpj } from "helpers/string";

export const normalizeText = text => {
	return text
		.toString()
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.toLowerCase()
		.trim();
};

export const CONTRACT_TYPE_MAPPING = [
	{ CSV: "NDA", value: "nda" },
	{ CSV: "Confidencialidade", value: "confidentiality" },
	{ CSV: "Comodato", value: "loan" },
	{ CSV: "Prestação de Serviço", value: "serviceProvision" },
	{ CSV: "Aditivo contratual", value: "addendum" },
	{ CSV: "Distrato", value: "termination" },
	{ CSV: "Outros", value: "other" }
];

export const MODALITY_MAPPING = [
	{ CSV: "Presencial", value: "onsite" },
	{ CSV: "Hibrido", value: "hybrid" },
	{ CSV: "Remoto", value: "remote" }
];

export const PRIORITY_MAPPING = [
	{ CSV: "Urgente", value: "urgent" },
	{ CSV: "Alta", value: "high" },
	{ CSV: "Média", value: "medium" },
	{ CSV: "Media", value: "medium" },
	{ CSV: "Baixa", value: "low" },
	{ CSV: "Urgent", value: "urgent" },
	{ CSV: "High", value: "high" },
	{ CSV: "Medium", value: "medium" },
	{ CSV: "Low", value: "low" }
];

export const TIER_MAPPING = [
	{ CSV: "High", value: "high" },
	{ CSV: "Mid", value: "mid" },
	{ CSV: "Low", value: "low" },
	{ CSV: "Alto", value: "high" },
	{ CSV: "Medio", value: "mid" },
	{ CSV: "Baixo", value: "low" }
];

export const mapCSVValue = (fieldName, csvValue, mappings) => {
	if (!csvValue || typeof csvValue !== "string") return csvValue;

	const normalizedCSV = normalizeText(csvValue);
	const mapping = mappings.find(
		item => normalizeText(item.CSV) === normalizedCSV
	);

	return mapping ? mapping.value : csvValue;
};

export const validateRequiredField = (value, fieldName, minLength = 1) => {
	if (!value || value.toString().trim().length < minLength) {
		return `Campo ${fieldName} é obrigatório${
			minLength > 1 ? ` e deve ter pelo menos ${minLength} caracteres` : ""
		}`;
	}
	return null;
};

export const validateBusinessName = value => {
	return validateRequiredField(value, "RAZAO SOCIAL", 2);
};

export const validateCNPJ = (value, hireds = []) => {
	const requiredError = validateRequiredField(value, "CNPJ");
	if (requiredError) return requiredError;

	if (!CNPJValidation(value)) {
		return "CNPJ inválido";
	}

	if (hireds && hireds.length > 0) {
		const cleanDocument = cleanCpfCnpj(value);
		const hiredExists = hireds.some(hired => {
			if (!hired.cnpj) return false;
			const cleanHiredDocument = cleanCpfCnpj(hired.cnpj);
			return cleanHiredDocument === cleanDocument;
		});

		if (!hiredExists) {
			return `Prestador com CNPJ ${value} não encontrado para a contratante selecionada`;
		}
	}

	return null;
};

export const validateContractType = value => {
	const requiredError = validateRequiredField(value, "TIPO DE CONTRATO");
	if (requiredError) return requiredError;

	const normalizedValue = normalizeText(value);
	const validContractType = CONTRACT_TYPE_MAPPING.find(
		item => normalizeText(item.CSV) === normalizedValue
	);

	if (!validContractType) {
		const validOptions = CONTRACT_TYPE_MAPPING.map(item => item.CSV).join(", ");
		return `Tipo de contrato inválido. Valores permitidos: ${validOptions}`;
	}

	return null;
};

export const validateRole = value => {
	return validateRequiredField(value, "FUNCAO", 2);
};

export const validateActivitiesDescription = value => {
	return validateRequiredField(value, "ATIVIDADES REALIZADAS", 2);
};

export const validateModality = value => {
	const requiredError = validateRequiredField(value, "MODALIDADE");
	if (requiredError) return requiredError;

	const normalizedValue = normalizeText(value);
	const validModality = MODALITY_MAPPING.find(
		item => normalizeText(item.CSV) === normalizedValue
	);

	if (!validModality) {
		const validOptions = MODALITY_MAPPING.map(item => item.CSV).join(", ");
		return `Modalidade inválida. Valores permitidos: ${validOptions}`;
	}

	return null;
};

export const validatePriority = value => {
	if (!value || value.toString().trim() === "") {
		return null;
	}

	const normalizedValue = normalizeText(value);
	const validPriority = PRIORITY_MAPPING.find(
		item => normalizeText(item.CSV) === normalizedValue
	);

	if (!validPriority) {
		return `Prioridade inválida. Valores permitidos: Urgente, Alta, Média, Baixa`;
	}

	return null;
};

export const validateTier = value => {
	if (!value || value.toString().trim() === "") {
		return null;
	}

	const normalizedValue = normalizeText(value);
	const validTier = TIER_MAPPING.find(
		item => normalizeText(item.CSV) === normalizedValue
	);

	if (!validTier) {
		return `Tier inválido. Valores permitidos: Low, Mid, High`;
	}

	return null;
};

export const validateValue = value => {
	const requiredError = validateRequiredField(value, "REMUNERACAO");
	if (requiredError) return requiredError;

	const numericValue = parseInt(value.replace("R$", "").trim());
	if (isNaN(numericValue) || numericValue <= 0) {
		return "Remuneração deve ser um número positivo";
	}

	return null;
};

export const validatePaymentDate = value => {
	const requiredError = validateRequiredField(value, "DATA DO PAGAMENTO");
	if (requiredError) return requiredError;

	return null;
};

export const validateLifespan = value => {
	const requiredError = validateRequiredField(value, "VIGENCIA");
	if (requiredError) return requiredError;

	const numericValue = parseInt(value);
	if (isNaN(numericValue) || numericValue <= 0) {
		return "Vigência deve ser um número positivo";
	}

	if (numericValue > 999) {
		return "Vigência deve ser menor ou igual a 999 meses";
	}

	return null;
};

export const validateStartDate = value => {
	const requiredError = validateRequiredField(value, "DATA DE INICIO");
	if (requiredError) return requiredError;

	if (!isValidDateFormat(value)) {
		return "Data de início deve estar no formato DD/MM/AAAA";
	}

	return null;
};

export const validateAdditionalInformation = value => {
	if (value && typeof value !== "string") {
		return "Cláusulas e informações adicionais deve ser um texto";
	}

	return null;
};

export const isValidDateFormat = dateString => {
	const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
	if (!dateRegex.test(dateString)) return false;

	const parts = dateString.split("/");
	const day = parseInt(parts[0], 10);
	const month = parseInt(parts[1], 10) - 1;
	const year = parseInt(parts[2], 10);

	const date = new Date(year, month, day);
	return (
		date.getFullYear() === year &&
		date.getMonth() === month &&
		date.getDate() === day
	);
};

export const validateField = (fieldName, value, rowIndex, hireds = []) => {
	switch (fieldName) {
		case "RAZAO SOCIAL":
			return validateBusinessName(value);
		case "CNPJ":
			return validateCNPJ(value, hireds);
		case "TIPO DE CONTRATO":
			return validateContractType(value);
		case "FUNCAO":
			return validateRole(value);
		case "ATIVIDADES REALIZADAS":
			return validateActivitiesDescription(value);
		case "MODALIDADE":
			return validateModality(value);
		case "REMUNERACAO":
			return validateValue(value);
		case "DATA DO PAGAMENTO":
			return validatePaymentDate(value);
		case "VIGENCIA":
			return validateLifespan(value);
		case "DATA DE INICIO":
			return validateStartDate(value);
		case "CLAUSULAS E INFORMACOES ADICIONAIS":
			return validateAdditionalInformation(value);
		case "PRIORIDADE":
			return validatePriority(value);
		case "TIER":
			return validateTier(value);
		default:
			return null;
	}
};

export const validateCSVStructure = (headers, data) => {
	const validationErrors = [];

	if (!headers || headers.length === 0) {
		validationErrors.push({
			row: 1,
			column: "Geral",
			message: "Arquivo CSV não possui cabeçalho"
		});
		return validationErrors;
	}

	const CSV_REQUIRED_FIELDS = [
		"RAZAO SOCIAL",
		"CNPJ",
		"TIPO DE CONTRATO",
		"FUNCAO",
		"ATIVIDADES REALIZADAS",
		"MODALIDADE",
		"REMUNERACAO",
		"DATA DO PAGAMENTO",
		"VIGENCIA",
		"DATA DE INICIO",
		"CLAUSULAS E INFORMACOES ADICIONAIS"
	];

	const CSV_OPTIONAL_FIELDS = ["PRIORIDADE", "TIER"];

	const CSV_ALL_FIELDS = [...CSV_REQUIRED_FIELDS, ...CSV_OPTIONAL_FIELDS];

	const missingColumns = CSV_REQUIRED_FIELDS.filter(
		field => !headers.includes(field)
	);
	if (missingColumns.length > 0) {
		validationErrors.push({
			row: 1,
			column: "Geral",
			message: `Colunas obrigatórias faltantes: ${missingColumns.join(", ")}`
		});
	}

	const extraColumns = headers.filter(
		header => !CSV_ALL_FIELDS.includes(header) && header.trim() !== ""
	);
	if (extraColumns.length > 0) {
		validationErrors.push({
			row: 1,
			column: "Geral",
			message: `Colunas não reconhecidas: ${extraColumns.join(", ")}`
		});
	}

	if (data.length === 0) {
		validationErrors.push({
			row: 2,
			column: "Geral",
			message: "Arquivo XLSX não contém dados, apenas cabeçalho"
		});
	}

	return validationErrors;
};

export default {
	normalizeText,
	CONTRACT_TYPE_MAPPING,
	MODALITY_MAPPING,
	PRIORITY_MAPPING,
	TIER_MAPPING,
	mapCSVValue,
	validateRequiredField,
	validateBusinessName,
	validateCNPJ,
	validateContractType,
	validateRole,
	validateActivitiesDescription,
	validateModality,
	validatePriority,
	validateTier,
	validateValue,
	validatePaymentDate,
	validateLifespan,
	validateStartDate,
	validateAdditionalInformation,
	isValidDateFormat,
	validateField,
	validateCSVStructure
};
